import { notify } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Loader2, ShieldCheck, Wrench, Package, Layers, Percent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { TabBar } from '@/components/ui/tab-bar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { countOf } from '@/lib/plural';

type SubTab = 'taxRates' | 'categories' | 'products';

interface TemplateTaxRate {
  id: string;
  name: string;
  rate: number;
  type: string;
  description?: string | null;
}

interface TemplateCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  parentId?: string | null;
  taxes?: { taxRate: TemplateTaxRate }[];
}

interface TemplateProduct {
  id: string;
  name: string;
  slug: string;
  sku: string;
  hsCode?: string | null;
  uom?: string | null;
  costPrice?: number | null;
  salePrice?: number | null;
  category?: { name: string } | null;
}

interface FbrReport {
  total: number;
  valid: number;
  invalid: number;
  fixed: number;
  report: Array<{ sku: string; name: string; valid: boolean; errors: string[]; oldUom?: string; newUom?: string; fixApplied: boolean }>;
}

const slugify = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function GlobalTemplatesTab() {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState<SubTab>('taxRates');

  const { data: stats } = useQuery({
    queryKey: ['tpl-stats'],
    queryFn: () => api.get('/platform/product-templates/stats').then((r) => r.data),
    staleTime: 30_000,
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Tax Rates', value: stats?.taxRates ?? '—', icon: Percent },
          { label: 'Categories', value: stats?.categories ?? '—', icon: Layers },
          { label: 'Products', value: stats?.products ?? '—', icon: Package },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
                <s.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <TabBar
        tabs={[
          { key: 'taxRates', label: 'Tax Rates', icon: Percent },
          { key: 'categories', label: 'Categories', icon: Layers },
          { key: 'products', label: 'Products', icon: Package },
        ]}
        activeTab={subTab}
        onTabChange={(key) => setSubTab(key as SubTab)}
      />

      {subTab === 'taxRates' && <TaxRatesSection queryClient={queryClient} />}
      {subTab === 'categories' && <CategoriesSection queryClient={queryClient} />}
      {subTab === 'products' && <ProductsSection queryClient={queryClient} />}
    </div>
  );
}

type QC = ReturnType<typeof useQueryClient>;

function TaxRatesSection({ queryClient }: { queryClient: QC }) {
  const { t } = useTranslation('platform');
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', rate: 0, type: 'EXCLUSIVE', description: '' });

  const { data: rates = [], isLoading } = useQuery<TemplateTaxRate[]>({
    queryKey: ['tpl-tax-rates'],
    queryFn: () => api.get('/platform/product-templates/tax-rates').then((r) => r.data),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tpl-tax-rates'] });
    queryClient.invalidateQueries({ queryKey: ['tpl-stats'] });
  };

  const createMutation = useMutation({
    mutationFn: () => api.post('/platform/product-templates/tax-rates', form).then((r) => r.data),
    onSuccess: () => {
      toast.success('Tax rate created');
      invalidate();
      setOpen(false);
      setForm({ name: '', rate: 0, type: 'EXCLUSIVE', description: '' });
    },
    onError: (e) => notify.error(e, 'Failed to create tax rate'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/product-templates/tax-rates/${id}`),
    onSuccess: () => {
      toast.success('Tax rate deleted');
      invalidate();
      setDeleteId(null);
    },
    onError: (e) => notify.error(e, 'Failed to delete'),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="me-2 h-4 w-4" /> New Tax Rate
        </Button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={4} cols={4} />
      ) : rates.length === 0 ? (
        <EmptyState icon={Percent} title="No template tax rates" description="Create a global tax rate to seed new tenants." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('colName')}</Th>
                  <Th>{t('colRate')}</Th>
                  <Th>{t('colType')}</Th>
                  <Th className="text-end">{t('colActions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {rates.map((r) => (
                  <Tr key={r.id}>
                    <Td className="td-name font-medium">{r.name}</Td>
                    <Td label={t('colRate')}>{r.rate}%</Td>
                    <Td label={t('colType')}>{r.type}</Td>
                    <Td className="td-actions text-end">
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(r.id)}>
                        <Trash2 className="h-4 w-4 text-destructive-text" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog visible={open} onHide={() => setOpen(false)} header="New Template Tax Rate" style={{ width: '28rem' }}>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="GST 18%" />
          </div>
          <div>
            <Label>Rate (%)</Label>
            <Input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Type</Label>
            <Dropdown
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.value })}
              options={[
                { label: 'Exclusive', value: 'EXCLUSIVE' },
                { label: 'Inclusive', value: 'INCLUSIVE' },
              ]}
              className="w-full"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="flex-1" disabled={!form.name || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Delete tax rate?"
        message="This template tax rate will be permanently removed."
        variant="danger"
        confirmLabel="Delete"
      />
    </div>
  );
}

function CategoriesSection({ queryClient }: { queryClient: QC }) {
  const { t } = useTranslation('platform');
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '' });

  const { data: categories = [], isLoading } = useQuery<TemplateCategory[]>({
    queryKey: ['tpl-categories'],
    queryFn: () => api.get('/platform/product-templates/categories').then((r) => r.data),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tpl-categories'] });
    queryClient.invalidateQueries({ queryKey: ['tpl-stats'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api
        .post('/platform/product-templates/categories', {
          name: form.name,
          slug: form.slug || slugify(form.name),
          description: form.description || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Category created');
      invalidate();
      setOpen(false);
      setForm({ name: '', slug: '', description: '' });
    },
    onError: (e) => notify.error(e, 'Failed to create category'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/product-templates/categories/${id}`),
    onSuccess: () => {
      toast.success('Category deleted');
      invalidate();
      setDeleteId(null);
    },
    onError: (e) => notify.error(e, 'Failed to delete'),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="me-2 h-4 w-4" /> New Category
        </Button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={4} cols={3} />
      ) : categories.length === 0 ? (
        <EmptyState icon={Layers} title="No template categories" description="Create a global category to seed new tenants." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('colName')}</Th>
                  <Th>{t('colSlug')}</Th>
                  <Th>{t('colTaxes')}</Th>
                  <Th className="text-end">{t('colActions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {categories.map((c) => (
                  <Tr key={c.id}>
                    <Td className="td-name font-medium">{c.name}</Td>
                    <Td label={t('colSlug')} className="text-muted-foreground">{c.slug}</Td>
                    <Td label={t('colTaxes')}>{c.taxes?.map((ct) => ct.taxRate.name).join(', ') || '—'}</Td>
                    <Td className="td-actions text-end">
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive-text" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog visible={open} onHide={() => setOpen(false)} header="New Template Category" style={{ width: '28rem' }}>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
              placeholder="Beverages"
            />
          </div>
          <div>
            <Label>Slug</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="beverages" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="flex-1" disabled={!form.name || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Delete category?"
        message="This template category will be permanently removed."
        variant="danger"
        confirmLabel="Delete"
      />
    </div>
  );
}

function ProductsSection({ queryClient }: { queryClient: QC }) {
  const { t } = useTranslation('platform');
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [fbrReport, setFbrReport] = useState<FbrReport | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    sku: '',
    categoryId: '',
    costPrice: 0,
    salePrice: 0,
    uom: '',
    hsCode: '',
  });

  const { data: productsResp, isLoading } = useQuery<{ data: TemplateProduct[]; meta: { total: number } }>({
    queryKey: ['tpl-products', search],
    queryFn: () =>
      api
        .get('/platform/product-templates/products', { params: { search: search || undefined, limit: 100 } })
        .then((r) => r.data),
  });
  const products = productsResp?.data ?? [];

  const { data: categories = [] } = useQuery<TemplateCategory[]>({
    queryKey: ['tpl-categories'],
    queryFn: () => api.get('/platform/product-templates/categories').then((r) => r.data),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tpl-products'] });
    queryClient.invalidateQueries({ queryKey: ['tpl-stats'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api
        .post('/platform/product-templates/products', {
          name: form.name,
          slug: form.slug || slugify(form.name),
          sku: form.sku,
          categoryId: form.categoryId || undefined,
          costPrice: form.costPrice || undefined,
          salePrice: form.salePrice || undefined,
          uom: form.uom || undefined,
          hsCode: form.hsCode || undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Product created');
      invalidate();
      setOpen(false);
      setForm({ name: '', slug: '', sku: '', categoryId: '', costPrice: 0, salePrice: 0, uom: '', hsCode: '' });
    },
    onError: (e) => notify.error(e, 'Failed to create product'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/platform/product-templates/products/${id}`),
    onSuccess: () => {
      toast.success('Product deleted');
      invalidate();
      setDeleteId(null);
    },
    onError: (e) => notify.error(e, 'Failed to delete'),
  });

  const validateMutation = useMutation({
    mutationFn: () => api.post('/platform/product-templates/validate-fbr').then((r) => r.data as FbrReport),
    onSuccess: (data) => {
      setFbrReport(data);
      toast.success(`Validated ${data.total}: ${data.valid} valid, ${data.invalid} invalid`);
    },
    onError: (e) => notify.error(e, 'Validation failed'),
  });

  const fixMutation = useMutation({
    mutationFn: () => api.post('/platform/product-templates/fix-fbr').then((r) => r.data as FbrReport),
    onSuccess: (data) => {
      setFbrReport(data);
      invalidate();
      toast.success(`Fixed ${countOf(data.fixed, 'product', 'products')}; ${data.valid} now valid`);
    },
    onError: (e) => notify.error(e, 'Fix failed'),
  });

  const categoryOptions = categories.map((c) => ({ label: c.name, value: c.id }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, SKU, HS code…"
          className="max-w-xs"
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={validateMutation.isPending} onClick={() => validateMutation.mutate()}>
            {validateMutation.isPending ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="me-2 h-4 w-4" />}
            Validate FBR
          </Button>
          <Button variant="outline" size="sm" disabled={fixMutation.isPending} onClick={() => fixMutation.mutate()}>
            {fixMutation.isPending ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Wrench className="me-2 h-4 w-4" />}
            Fix FBR
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="me-2 h-4 w-4" /> New Product
          </Button>
        </div>
      </div>

      {fbrReport && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="font-medium">
                FBR Report — {fbrReport.valid}/{fbrReport.total} valid
                {fbrReport.fixed > 0 && `, ${fbrReport.fixed} fixed`}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setFbrReport(null)}>Dismiss</Button>
            </div>
            {fbrReport.invalid > 0 && (
              <div className="max-h-48 overflow-auto text-xs space-y-1">
                {fbrReport.report.filter((r) => !r.valid).map((r) => (
                  <div key={r.sku} className="text-destructive-text">
                    <span className="font-mono">{r.sku}</span> — {r.errors.join('; ')}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : products.length === 0 ? (
        <EmptyState icon={Package} title="No template products" description="Create a global product to seed new tenants." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('colName')}</Th>
                  <Th>{t('colSKU')}</Th>
                  <Th>{t('colCategory')}</Th>
                  <Th>{t('colHSCode')}</Th>
                  <Th className="text-end">{t('colSalePrice')}</Th>
                  <Th className="text-end">{t('colActions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {products.map((p) => (
                  <Tr key={p.id}>
                    <Td className="td-name font-medium">{p.name}</Td>
                    <Td label={t('colSKU')} className="font-mono text-xs">{p.sku}</Td>
                    <Td label={t('colCategory')}>{p.category?.name ?? '—'}</Td>
                    <Td label={t('colHSCode')} className="text-muted-foreground">{p.hsCode || '—'}</Td>
                    <Td label={t('colSalePrice')} className="text-end">{p.salePrice != null ? formatCurrency(p.salePrice) : '—'}</Td>
                    <Td className="td-actions text-end">
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(p.id)}>
                        <Trash2 className="h-4 w-4 text-destructive-text" />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog visible={open} onHide={() => setOpen(false)} header="New Template Product" style={{ width: '32rem' }}>
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="col-span-2">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
            />
          </div>
          <div>
            <Label>SKU</Label>
            <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          </div>
          <div>
            <Label>Slug</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Category</Label>
            <Dropdown
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.value })}
              options={categoryOptions}
              placeholder="Select category"
              filter
              showClear
              className="w-full"
            />
          </div>
          <div>
            <Label>Cost Price</Label>
            <Input type="number" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Sale Price</Label>
            <Input type="number" value={form.salePrice} onChange={(e) => setForm({ ...form, salePrice: Number(e.target.value) })} />
          </div>
          <div>
            <Label>UOM</Label>
            <Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="PCS" />
          </div>
          <div>
            <Label>HS Code</Label>
            <Input value={form.hsCode} onChange={(e) => setForm({ ...form, hsCode: e.target.value })} placeholder="0000.0000" />
          </div>
          <div className="col-span-2 flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              className="flex-1"
              disabled={!form.name || !form.sku || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Delete product?"
        message="This template product will be permanently removed."
        variant="danger"
        confirmLabel="Delete"
      />
    </div>
  );
}
